<template>
  <div class="first-box">
    <template v-if="subscription">
      <div class="labels text-body-1 text-secondary-1">
        <Grid
          v-for="(label, index) of labels"
          :key="label.label"
          :class="{ visible: index < visibleLabelsCount || showMore }"
          class="label-wrapper"
          align-items="center"
          justify-items="start"
          gtc="110px 1fr"
        >
          <div class="label">{{ label.label }}:</div>

          <Grid gaf="column" justify-content="start" align-items="center" class="value">
            <RInfo :link="label.link" :to="label.to" :text="label.value" />

            <template v-if="label.hasValue2">
              <span class="q-px-xs text-grey3">/</span>
              <RInfo :link="label.link" :to="label.to" :text="label.value2" />
            </template>

            <div v-if="label.editable">
              <EditIcon class="q-ml-sm cursor-pointer edit-icon" />
              <RPopupEdit
                :value="editableFields[label.editable.field]"
                :title="label.editable.title"
                @ok="updateField({ ...$event, field: label.editable.field })"
              >
                <template v-slot="props">
                  <RInput
                    v-model="props.model.value"
                    :disable="props.loading"
                    no-feedback-icon
                    no-hint
                    no-label
                    autofocus
                    on-clear="focus"
                    @keyup.enter.stop="updateField({ ...props, field: label.editable.field })"
                  />
                </template>
              </RPopupEdit>
            </div>
          </Grid>
        </Grid>

        <ShowMoreLess v-model="showMore" />
      </div>
    </template>

    <div v-else>Loading</div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
// Components
import ShowMoreLess from '@/reusable/ShowMoreLess'
import EditIcon from '@/ui/icons/EditIcon'
import RPopupEdit from '@/ui/RPopupEdit'

export default {
  components: {
    ShowMoreLess,
    EditIcon,
    RPopupEdit
  },

  data: () => ({
    visibleLabelsCount: 5,
    showMore: false,
    editableFields: {
      publisher_reference: null,
      library_reference: null
    }
  }),

  computed: {
    ...mapGetters('subscription-show', ['subscription']),

    labels() {
      if (!this.subscription) return

      const sub = this.subscription

      return [
        {
          label: 'Publisher',
          value: sub.publisher?.name,
          link: !!sub.publisher,
          to: `/publishers/${sub.publisher?.id}`
        },
        {
          label: 'Vendor',
          value: sub.vendor?.name,
          link: !!sub.vendor,
          to: `/vendors/${sub.vendor?.id}`
        },

        {
          label: 'Product',
          value: sub.product?.name,
          link: !!sub.product,
          to: `/products/${sub.product?.id}`
        },

        {
          label: 'Product type',
          value: sub.product.type?.name
        },
        {
          label: 'Managed by',
          value: sub.owner?.name
        },
        {
          label: 'ISSN/eISSN',
          value: sub.product.print_identifier,
          value2: sub.product.online_identifier,
          hasValue2: true
        },
        {
          label: 'Publisher reference',
          value: this.editableFields.publisher_reference,
          editable: {
            field: 'publisher_reference',
            title: 'Update publisher reference'
          }
        },
        {
          label: 'Library reference',
          value: this.editableFields.library_reference,
          editable: {
            field: 'library_reference',
            title: 'Update library reference'
          }
        }
      ]
    }
  },

  watch: {
    subscription: {
      handler(val) {
        if (val) {
          this.editableFields.publisher_reference = val.publisher_reference
          this.editableFields.library_reference = val.library_reference
        }
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    ...mapActions('subscription-show', ['updateSubscription']),

    updateField({ set, field, cancel, value, setLoading }) {
      setLoading(true)

      this.updateSubscription({
        id: this.subscription.id,
        key: field,
        payload: { [field]: value }
      })
        .then(set)
        .catch(cancel)
        .finally(() => setLoading(false))
    }
  }
}
</script>

<style lang="scss" scoped>
.first-box {
  position: relative;

  .labels {
    .label-wrapper:not(.visible) {
      display: none;
    }

    .label {
      width: 110px;
      height: var(--r-space-lg);
      line-height: var(--r-space-lg);
    }

    .value {
      .edit-icon {
        visibility: hidden;
      }

      &:hover .edit-icon {
        visibility: visible;
      }
    }
  }

  .show-more-less {
    position: absolute;
    justify-content: center;
    bottom: -12px;
    width: 100%;
  }
}
</style>
